import Config from "../_config/config";
import myAxios from "./axiositeachWrapper";

const BASE = `${Config.nodeSiteUrl}v1/credentials`;

/** Predicate JSON grammar — mirrors nodefork's services/credentials/rules/rule.types.ts */
export type ComparisonOperator = ">=" | "<=" | "=" | ">" | "<" | "!=";
export type ScopeType = "lesson_plan" | "course" | "program" | "batch";

export type Clause =
  | { op: "and"; clauses: Clause[] }
  | { op: "or"; clauses: Clause[] }
  | { op: "not"; clause: Clause }
  | { type: "scope_completed"; scopeType: "lesson_plan" | "course" | "program"; scopeId: number }
  | { type: "attendance_percent"; operator: ComparisonOperator; value: number; scopeType: "course" | "batch"; scopeId: number }
  | { type: "test_score"; operator: ComparisonOperator; value: number; testId: number }
  | { type: "login_streak"; minDays: number }
  | { type: "enrollment_age"; operator: ComparisonOperator; valueDays: number }
  | { type: "submission_count"; scopeType: "lesson_plan" | "course" | "program"; scopeId: number; operator: ComparisonOperator; value: number }
  | { type: "badge_held"; badgeDefinitionId: number };

export type TriggerEvent =
  | "resource_completion"
  | "test_submission"
  | "attendance_update"
  | "scheduled"
  | "manual";

export interface TriggerConfig {
  events: TriggerEvent[];
  scopeFilter?: { scopeType: ScopeType; scopeId: number } | null;
}

export interface Rule {
  id: number;
  ruleUUID: string | null;
  badgeDefinitionId: number;
  badgeName?: string | null;
  badgeAchievementType?: string | null;
  name: string;
  description?: string | null;
  predicate: Clause | null;
  triggers: TriggerConfig | null;
  reviewRequired: boolean;
  reviewerUserIds: number[];
  notifyOnIssue: boolean;
  enableStatus: boolean;
  scopeType?: string | null;
  scopeId?: number | null;
  fires7d?: number;
  pendingEligibility?: number;
  createdDate?: string | null;
  updatedDate?: string | null;
}

export interface RuleEvaluationLogEntry {
  id: number;
  ruleId: number;
  enquiryId: number;
  studentName?: string | null;
  studentCode?: string | null;
  triggerType: string;
  result: "matched" | "not_matched" | "error";
  resultDetails: any;
  issuedCredentialId: number | null;
  createdDate: string;
}

export interface EligibilityRow {
  id: number;
  ruleId: number;
  enquiryId: number;
  studentName?: string | null;
  studentCode?: string | null;
  studentEmail?: string | null;
  eligibleDate: string;
  status: "pending" | "approved" | "rejected";
  reviewedBy?: number | null;
  reviewedDate?: string | null;
  reviewNote?: string | null;
  issuedCredentialId?: number | null;
}

class RulesService {
  list = (params: { page?: number; limit?: number; badgeDefinitionId?: number } = {}) =>
    myAxios.get<any>(`${BASE}/admin/rules`, { params });

  get = (id: number) => myAxios.get<any>(`${BASE}/admin/rules/${id}`);

  create = (body: {
    badgeDefinitionId: number;
    name: string;
    description?: string;
    predicate: Clause;
    triggers: TriggerConfig;
    reviewRequired?: boolean;
    reviewerUserIds?: number[];
    notifyOnIssue?: boolean;
  }) => myAxios.post<any>(`${BASE}/admin/rules`, body);

  update = (
    id: number,
    body: Partial<{
      name: string;
      description: string;
      predicate: Clause;
      triggers: TriggerConfig;
      reviewRequired: boolean;
      reviewerUserIds: number[];
      notifyOnIssue: boolean;
      enableStatus: boolean;
    }>,
  ) => myAxios.put<any>(`${BASE}/admin/rules/${id}`, body);

  remove = (id: number) => myAxios.delete<any>(`${BASE}/admin/rules/${id}`);

  dryRun = (id: number, enquiryId: number) =>
    myAxios.post<any>(`${BASE}/admin/rules/${id}/dry-run`, { enquiryId });

  reevaluate = (id: number, enquiryIds?: number[]) =>
    myAxios.post<any>(
      `${BASE}/admin/rules/${id}/reevaluate`,
      enquiryIds && enquiryIds.length ? { enquiryIds } : {},
    );

  log = (id: number, params: { page?: number; limit?: number; result?: string } = {}) =>
    myAxios.get<any>(`${BASE}/admin/rules/${id}/log`, { params });

  eligibility = (
    id: number,
    params: { status?: "pending" | "approved" | "rejected"; page?: number; limit?: number } = {},
  ) => myAxios.get<any>(`${BASE}/admin/rules/${id}/eligibility`, { params });

  approveEligibility = (eligibilityId: number, note?: string) =>
    myAxios.post<any>(`${BASE}/admin/rules/eligibility/${eligibilityId}/approve`, { note });

  rejectEligibility = (eligibilityId: number, note?: string) =>
    myAxios.post<any>(`${BASE}/admin/rules/eligibility/${eligibilityId}/reject`, { note });

  searchReviewerUsers = (q: string) =>
    myAxios.get<any>(`${BASE}/admin/rules/users/search`, { params: { q } });

  /** Hydrate previously-saved reviewer IDs into display info on rule edit. */
  hydrateReviewerUsers = (ids: number[]) =>
    myAxios.get<any>(`${BASE}/admin/rules/users/search`, {
      params: { ids: ids.join(",") },
    });
}

export default new RulesService();
